import { ArrowRight, Gift, ShieldCheck } from "lucide-react";
import { Section, SectionHeading, Card } from "./service-revops-ui";
import { SectionCta } from "./service-revops-section-cta";
import { gohighlevelConfig as defaultConfig } from "./service-platform-config";
import type { PlatformConfig } from "./service-platform-config";
import { AVATAR_WEBP } from "@/lib/optimizedImages";

export function ServicePricing({
  config = defaultConfig,
}: {
  config?: PlatformConfig;
} = {}) {
  return (
    <Section max="5xl">
      <SectionHeading
        eyebrow={config.pricingEyebrow}
        navy={config.pricingHeadlineNavy}
        blue={config.pricingHeadlineBlue}
      />
      <div
        className="rounded-[24px] p-7 mb-8 text-center"
        style={{
          background: "linear-gradient(135deg, #F0F8FF 0%, #EAF5FF 100%)",
          border: "1px solid rgba(11,124,242,0.10)",
        }}
      >
        <p
          className="font-display font-bold max-w-2xl mx-auto"
          style={{
            fontSize: "18px",
            lineHeight: 1.5,
            color: "#071E61",
            letterSpacing: "-0.02em",
          }}
        >
          {config.pricingIntro}{" "}
          <span style={{ color: "#0B7CF2" }}>
            {config.pricingIntroHighlight}
          </span>
        </p>
      </div>
      <div
        className="overflow-hidden rounded-[24px] overflow-x-auto"
        style={{
          background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBFF 100%)",
          border: "1px solid rgba(8,46,103,0.07)",
          boxShadow: "0 14px 40px rgba(8,46,103,0.08)",
        }}
      >
        <table className="comparison-table">
          <thead>
            <tr>
              <th>Capability</th>
              <th>{config.platformName} Architect</th>
              <th>General VA</th>
              <th>US Agency</th>
            </tr>
          </thead>
          <tbody>
            {config.pricingRows.map((row, i) => (
              <tr key={i}>
                <td>{row.capability}</td>
                <td>{row.architect}</td>
                <td>{row.va}</td>
                <td>{row.agency}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

export function ServiceFreeAudit({
  config = defaultConfig,
}: {
  config?: PlatformConfig;
} = {}) {
  const deliverables = config.freeAuditDeliverables;
  return (
    <Section max="5xl">
      <div
        className="relative rounded-[32px] md:rounded-[36px] p-8 sm:p-12 md:p-16 overflow-hidden text-center transition-all duration-300"
        style={{
          background:
            "linear-gradient(180deg, #FFFFFF 0%, #F6FAFF 50%, #EEF6FF 100%)",
          border: "1.5px solid rgba(11, 124, 242, 0.16)",
          boxShadow:
            "0 24px 68px rgba(8, 46, 103, 0.08), 0 4px 16px rgba(11, 124, 242, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.9)",
        }}
      >
        {/* Soft multi-layered blue atmospheric glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 select-none z-0"
          style={{
            backgroundImage: `
              radial-gradient(circle at 50% 0%, rgba(11, 124, 242, 0.14) 0%, rgba(11, 124, 242, 0.03) 45%, transparent 70%),
              radial-gradient(circle at 10% 85%, rgba(68, 242, 99, 0.07) 0%, transparent 40%),
              radial-gradient(circle at 90% 85%, rgba(11, 124, 242, 0.06) 0%, transparent 45%)
            `,
          }}
        />

        {/* Subtle decorative curved architectural accents */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute -top-8 -right-8 w-64 h-64 select-none opacity-40 z-0"
          viewBox="0 0 200 200"
          fill="none"
        >
          <circle
            cx="160"
            cy="40"
            r="120"
            stroke="rgba(11, 124, 242, 0.12)"
            strokeWidth="1.5"
          />
          <circle
            cx="160"
            cy="40"
            r="80"
            stroke="rgba(11, 124, 242, 0.08)"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
        </svg>

        <div className="relative z-10 max-w-3xl mx-auto">
          {/* Eyebrow badge with glowing gift icon */}
          <div
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-7"
            style={{
              background: "rgba(11, 124, 242, 0.08)",
              border: "1px solid rgba(11, 124, 242, 0.18)",
              fontSize: "11px",
              color: "#0B7CF2",
            }}
          >
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "#0B7CF2", color: "#FFFFFF" }}
            >
              <Gift className="w-3 h-3" />
            </div>
            <span>{config.freeAuditBadge}</span>
          </div>

          {/* Impactful headline with dual-tone navy + electric blue */}
          <h2
            className="font-display font-black tracking-[-0.04em] mb-5"
            style={{
              fontSize: "clamp(32px, 4.4vw, 52px)",
              lineHeight: 1.05,
              color: "#071E61",
            }}
          >
            {config.freeAuditHeadlinePre}{" "}
            <span
              className="block sm:inline"
              style={{
                color: "#0B7CF2",
                textShadow: "0 2px 20px rgba(11, 124, 242, 0.18)",
              }}
            >
              {config.freeAuditHeadlineBlue}
            </span>
          </h2>

          {/* Clean, persuasive description */}
          <p
            className="mx-auto mb-9 max-w-2xl font-sans"
            style={{
              fontSize: "16.5px",
              lineHeight: 1.6,
              color: "#476182",
            }}
          >
            {config.freeAuditDesc}
          </p>

          {/* Value cards with tangible deliverables */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 mb-9 text-left">
            {deliverables.map((item) => (
              <div
                key={item.title}
                className="group relative rounded-[20px] p-5 transition-all duration-200"
                style={{
                  background: "rgba(255, 255, 255, 0.92)",
                  border: "1px solid rgba(11, 124, 242, 0.14)",
                  boxShadow: "0 8px 24px rgba(8, 46, 103, 0.04)",
                }}
              >
                <div
                  className="inline-block px-2 py-0.5 rounded-full font-sans font-bold uppercase tracking-[0.1em] mb-2.5"
                  style={{
                    fontSize: "9.5px",
                    background: "rgba(11, 124, 242, 0.08)",
                    color: "#0B7CF2",
                  }}
                >
                  {item.tag}
                </div>
                <h3
                  className="font-display font-bold mb-1.5"
                  style={{
                    fontSize: "15px",
                    lineHeight: 1.25,
                    color: "#071E61",
                    letterSpacing: "-0.015em",
                  }}
                >
                  {item.title}
                </h3>
                <p
                  className="font-sans"
                  style={{
                    fontSize: "12.5px",
                    lineHeight: 1.45,
                    color: "#536B89",
                  }}
                >
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Primary CTA Button — signature brand action */}
          <div className="flex flex-col items-center justify-center">
            <a
              href="/book"
              className="group inline-flex items-center justify-center gap-2.5 px-9 h-[58px] sm:h-[62px] rounded-full font-sans font-bold text-[16px] text-white transition-all duration-300 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0B7CF2]"
              style={{
                background: "linear-gradient(135deg, #0B7CF2 0%, #1793F7 100%)",
                boxShadow:
                  "0 14px 34px rgba(11, 124, 242, 0.28), 0 2px 8px rgba(11, 124, 242, 0.12)",
              }}
            >
              <span>{config.freeAuditCtaLabel}</span>
              <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
            </a>

            {/* Micro-trust indicators */}
            <div
              className="flex items-center justify-center flex-wrap gap-4 sm:gap-6 mt-4 font-sans font-medium"
              style={{ fontSize: "13px", color: "#6A7D97" }}
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="text-base">&#9201;</span>
                <span>30 minutes</span>
              </span>
              <span className="text-slate-300">&middot;</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="text-base">&#128179;</span>
                <span>100% Free</span>
              </span>
              <span className="text-slate-300">&middot;</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="text-base">&#127919;</span>
                <span>Discovery only</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

export function ServiceWhoIAm({
  config = defaultConfig,
}: {
  config?: PlatformConfig;
} = {}) {
  return (
    <Section max="5xl">
      <SectionHeading
        eyebrow={config.whoEyebrow}
        navy={config.whoHeadlineNavy}
        blue={config.whoHeadlineBlue}
        breakLine={true}
      />
      <Card radius={26}>
        <div className="p-2 md:p-4">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            <div
              className="w-24 h-24 rounded-full overflow-hidden shrink-0"
              style={{ border: "3px solid rgba(11,124,242,0.25)" }}
            >
              <img
                src={AVATAR_WEBP}
                alt={config.whoImageAlt}
                width={96}
                height={96}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3
                className="font-display font-extrabold mb-2"
                style={{
                  fontSize: "26px",
                  color: "#071E61",
                  letterSpacing: "-0.03em",
                }}
              >
                {config.whoName}
              </h3>
              <p
                className="font-sans font-semibold uppercase tracking-[0.18em] mb-4"
                style={{ fontSize: "12px", color: "#0B7CF2" }}
              >
                {config.whoRole}
              </p>
              <p
                className="mb-6"
                style={{ fontSize: "16px", lineHeight: 1.6, color: "#536B89" }}
              >
                {config.whoBio}
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-2.5">
                {config.credentials.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 font-sans text-[12px] font-semibold"
                    style={{ background: "#EAF5FF", color: "#0B7CF2" }}
                  >
                    <ShieldCheck
                      className="w-3.5 h-3.5"
                      style={{ color: "#27DC5D" }}
                    />
                    {c}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Card>
      <SectionCta copy={config.whoCtaCopy} label={config.whoCtaLabel} />
    </Section>
  );
}
